/**
 * Share a document or a folder from ONE, as FusionDocs does: who has access and why, add people from ONE's directory
 * with "Can view" (default) or "Can edit", change or remove them. A folder share covers everything inside it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Lock, Share2, Trash2, X } from "lucide-react";
import { useState } from "react";
import { api } from "../../lib/api";
import type { Person } from "../../lib/work";
import { ErrorNote } from "../ui";
import { PersonPicker } from "../work";

type Level = "view" | "edit";
interface Member { email: string; name?: string | null; level: Level; via?: string; removable?: boolean }
interface Shares { available: boolean; canShare: boolean; owner?: { email: string; name?: string | null } | null; members: Member[] }
export type ShareTarget = { kind: "document"; id: string; title: string } | { kind: "folder"; path: string };

const url = (t: ShareTarget) => (t.kind === "document" ? `/api/fd/documents/${encodeURIComponent(t.id)}/shares` : `/api/fd/folders/shares?path=${encodeURIComponent(t.path)}`);
const putUrl = (t: ShareTarget) => (t.kind === "document" ? `/api/fd/documents/${encodeURIComponent(t.id)}/shares` : "/api/fd/folders/shares");
const why = (via?: string) => (!via || via === "file" || via === "folder" ? null : via.startsWith("folder:") ? `from folder ${via.slice(7)}` : via.startsWith("app:") ? `from ${via.slice(4)}` : via === "workspace" ? "always (your workspace)" : via);

export function ShareButton({ target, className }: { target: ShareTarget; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={className ?? "btn btn-sm"} onClick={() => setOpen(true)} aria-label="Share" title="Share"><Share2 size={15} />{className ? null : " Share"}</button>
      {open && <ShareDialog target={target} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareDialog({ target, onClose }: { target: ShareTarget; onClose: () => void }) {
  const qc = useQueryClient();
  const key = ["fd", "shares", target.kind === "document" ? target.id : target.path];
  const q = useQuery({ queryKey: key, queryFn: () => api<Shares>(url(target)) });
  const [who, setWho] = useState<Person[]>([]);
  const [level, setLevel] = useState<Level>("view");
  const change = useMutation({
    mutationFn: (b: { add?: Array<{ email: string; level: Level }>; remove?: string[]; set?: Array<{ email: string; level: Level }> }) =>
      api<Shares>(putUrl(target), { method: "PUT", json: target.kind === "folder" ? { path: target.path, ...b } : b }),
    onSuccess: (d) => { qc.setQueryData(key, d); setWho([]); void qc.invalidateQueries({ queryKey: ["fd", "doc"] }); },
  });
  const d = q.data;
  const emails = who.map((p) => p.email).filter((e): e is string => !!e);
  const name = target.kind === "document" ? target.title : target.path;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label={`Share ${name}`} onKeyDown={(e) => e.key === "Escape" && onClose()}>
      <div className="w-full max-w-lg rounded-t-2xl bg-[var(--surface,#fff)] p-4 shadow-xl sm:rounded-2xl">
        <div className="mb-3 flex items-start gap-2">
          <Share2 size={18} className="mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1"><h2 className="font-semibold">Share {target.kind === "folder" ? "folder" : "document"}</h2><p className="muted truncate text-sm">{name}</p></div>
          <button type="button" className="btn btn-sm" onClick={onClose} aria-label="Close"><X size={15} /></button>
        </div>
        {q.isLoading ? <p className="muted text-sm"><Loader2 size={14} className="mr-1 inline animate-spin" /> Loading who has access…</p>
          : q.error ? <ErrorNote error={q.error} />
          : !d?.available ? <p className="muted text-sm">Sharing from ONE switches on as soon as FusionDocs offers it — nothing to do on your side.</p>
          : (
            <div className="space-y-4 text-sm">
              {d.canShare ? (
                <div className="space-y-2">
                  <PersonPicker value={who} onChange={setWho} placeholder="Add people…" />
                  <div className="flex items-center gap-2">
                    <select className="input !w-auto" value={level} onChange={(e) => setLevel(e.target.value as Level)} aria-label="Access level">
                      <option value="view">Can view</option>
                      <option value="edit">Can edit</option>
                    </select>
                    <button type="button" className="btn btn-sm btn-primary" disabled={!emails.length || change.isPending} onClick={() => change.mutate({ add: emails.map((email) => ({ email, level })) })}>Share</button>
                  </div>
                  <p className="muted text-xs">{level === "view" ? "Can view: open, read and download." : "Can edit: also upload new versions, rename, move and add files."}{target.kind === "folder" ? " Covers everything in this folder, including documents added later." : ""}</p>
                </div>
              ) : <p className="muted flex items-center gap-1 text-xs"><Lock size={12} /> You can see who has access, but only the owner or someone with filing rights can change it.</p>}
              <ErrorNote error={change.error} />
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">Who has access</h3>
                <ul className="divide-y divide-[var(--line)]">
                  {d.owner && <li className="flex items-center gap-2 py-2"><span className="min-w-0 flex-1 truncate">{d.owner.name || d.owner.email}</span><span className="muted text-xs">Owner</span></li>}
                  {d.members.map((m) => (
                    <li key={m.email} className="flex items-center gap-2 py-2">
                      <div className="min-w-0 flex-1"><div className="truncate">{m.name || m.email}</div>{(m.name || why(m.via)) && <div className="muted truncate text-xs">{[m.name ? m.email : null, why(m.via)].filter(Boolean).join(" · ")}</div>}</div>
                      {d.canShare && m.removable ? (
                        <>
                          <select className="input !w-auto !py-1 text-xs" value={m.level} onChange={(e) => change.mutate({ set: [{ email: m.email, level: e.target.value as Level }] })} aria-label={`Access for ${m.email}`}>
                            <option value="view">Can view</option>
                            <option value="edit">Can edit</option>
                          </select>
                          <button type="button" className="btn btn-sm" aria-label={`Remove ${m.email}`} title="Remove access" onClick={() => change.mutate({ remove: [m.email] })}><Trash2 size={13} /></button>
                        </>
                      ) : <span className="muted text-xs">{m.level === "edit" ? "Can edit" : "Can view"}</span>}
                    </li>
                  ))}
                  {!d.members.length && !d.owner && <li className="muted py-2 text-xs">Only you.</li>}
                </ul>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
